/**
 * Règles de détection des charges récurrentes (banque + Wave).
 * Les règles proposent une catégorie à l'import ; rien n'est modifié sans validation.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Plus, Loader2, Trash2, Edit3, FlaskConical, Repeat, Tag, Sparkles, AlertTriangle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => (v == null ? '—' : Math.round(v).toLocaleString('fr-FR'));

const SOURCE_LABELS = { bank: 'Banque', wave: 'Wave', both: 'Banque + Wave' };
const DIRECTION_LABELS = { any: 'Tous sens', debit: 'Sorties', credit: 'Entrées' };

const EMPTY_FORM = {
  name: '', source: 'both', direction: 'debit', keywords: '', beneficiaries: '',
  amountMode: 'any', amountMin: '', amountExpected: '', tolerancePercent: 5,
  category: '', displayLabel: '', includeFees: true, priority: 50, isActive: true, notes: '',
};

const toPayload = (f) => ({
  name: f.name.trim(),
  source: f.source,
  direction: f.direction,
  keywords: f.keywords.split(',').map((s) => s.trim()).filter(Boolean),
  beneficiaries: f.beneficiaries.split(',').map((s) => s.trim()).filter(Boolean),
  amountMode: f.amountMode,
  amountMin: f.amountMode === 'min' ? Number(f.amountMin || 0) : null,
  amountExpected: f.amountMode === 'expected' ? Number(f.amountExpected || 0) : null,
  tolerancePercent: Number(f.tolerancePercent || 0),
  category: f.category,
  displayLabel: f.displayLabel.trim() || null,
  includeFees: !!f.includeFees,
  priority: Number(f.priority || 0),
  isActive: !!f.isActive,
  notes: f.notes.trim() || null,
});

const ruleToForm = (r) => ({
  name: r.name || '', source: r.source || 'both', direction: r.direction || 'any',
  keywords: (r.keywords || []).join(', '), beneficiaries: (r.beneficiaries || []).join(', '),
  amountMode: r.amountMode || 'any',
  amountMin: r.amountMin || '', amountExpected: r.amountExpected || '',
  tolerancePercent: r.tolerancePercent ?? 5,
  category: r.category || '', displayLabel: r.displayLabel || '',
  includeFees: !!r.includeFees, priority: r.priority ?? 0,
  isActive: r.isActive !== false, notes: r.notes || '',
});

export default function ExpenseRulesPanel() {
  const [rules, setRules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, c, rec] = await Promise.all([
        axios.get(`${API}/expense-rules`),
        axios.get(`${API}/bank/categories`),
        axios.get(`${API}/expense-rules/recurring`),
      ]);
      setRules(r.data?.items || []);
      setCategories(c.data?.items || []);
      setRecurring(rec.data?.items || []);
    } catch (e) {
      toast({ title: 'Chargement impossible', description: e?.response?.data?.detail || 'Erreur réseau', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = (draft) => {
    setEditingId(null);
    setTestResult(null);
    setForm(draft ? {
      ...EMPTY_FORM,
      ...draft,
      keywords: (draft.keywords || []).join(', '),
      beneficiaries: (draft.beneficiaries || []).join(', '),
      amountExpected: draft.amountExpected || '',
      tolerancePercent: draft.tolerancePercent ?? 10,
    } : EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (rule) => {
    setEditingId(rule.id);
    setTestResult(null);
    setForm(ruleToForm(rule));
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editingId) await axios.patch(`${API}/expense-rules/${editingId}`, toPayload(form));
      else await axios.post(`${API}/expense-rules`, toPayload(form));
      toast({ title: editingId ? 'Règle mise à jour' : 'Règle créée' });
      setOpen(false);
      load();
    } catch (e) {
      toast({ title: 'Enregistrement refusé', description: e?.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTestResult(null);
    try {
      const { data } = await axios.post(`${API}/expense-rules/test`, toPayload(form));
      setTestResult(data);
    } catch (e) {
      toast({ title: 'Test impossible', description: e?.response?.data?.detail || 'Erreur', variant: 'destructive' });
    }
  };

  const toggleActive = async (rule) => {
    try {
      await axios.patch(`${API}/expense-rules/${rule.id}`, { isActive: !rule.isActive });
      load();
    } catch (e) {
      toast({ title: 'Action impossible', description: e?.response?.data?.detail || 'Erreur', variant: 'destructive' });
    }
  };

  const remove = async (rule) => {
    if (!window.confirm(`Supprimer la règle « ${rule.name} » ?`)) return;
    try {
      await axios.delete(`${API}/expense-rules/${rule.id}`);
      toast({ title: 'Règle supprimée' });
      load();
    } catch (e) {
      toast({ title: 'Suppression impossible', description: e?.response?.data?.detail || 'Erreur', variant: 'destructive' });
    }
  };

  const criteria = (r) => {
    const parts = [];
    if (r.keywords?.length) parts.push(`libellé : ${r.keywords.slice(0, 3).join(' / ')}${r.keywords.length > 3 ? '…' : ''}`);
    if (r.beneficiaries?.length) parts.push(`${r.beneficiaries.length} bénéficiaire(s) déclaré(s)`);
    if (r.amountMode === 'min') parts.push(`montant ≥ ${fmt(r.amountMin)} XOF`);
    if (r.amountMode === 'expected') parts.push(`montant ${fmt(r.amountExpected)} XOF ± ${r.tolerancePercent}%`);
    return parts.join(' · ') || 'aucun critère';
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16" data-testid="expense-rules-loading">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="expense-rules-panel">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-slate-600 max-w-3xl">
          Ces règles reconnaissent vos charges récurrentes à l&apos;import, côté banque comme côté Wave,
          et <span className="font-medium">proposent</span> la catégorie : rien n&apos;est classé sans votre
          validation. Les frais Wave pris en charge suivent la catégorie du paiement.
        </p>
        <Button size="sm" onClick={() => openCreate(null)} data-testid="expense-rule-new-btn">
          <Plus className="h-4 w-4 mr-1" /> Nouvelle règle
        </Button>
      </div>

      <div className="space-y-2">
        {rules.map((r) => (
          <div
            key={r.id}
            className={`border rounded-lg p-3 flex items-start justify-between gap-4 ${r.isActive ? 'bg-white' : 'bg-slate-50'}`}
            data-testid={`expense-rule-${r.id}`}
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-sm font-medium ${r.isActive ? 'text-slate-900' : 'text-slate-400 line-through'}`}>
                  {r.name}
                </span>
                <Badge variant="outline" className="text-[10px]">{SOURCE_LABELS[r.source]}</Badge>
                <Badge variant="outline" className="text-[10px]">{DIRECTION_LABELS[r.direction]}</Badge>
                {r.includeFees && <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100 text-[10px]">frais inclus</Badge>}
              </div>
              <p className="text-xs text-slate-500 mt-1">{criteria(r)}</p>
              <p className="text-xs mt-1">
                <Tag className="h-3 w-3 inline mr-1 text-slate-400" />
                <span className="text-slate-700">{r.displayLabel || r.categoryLabel}</span>
                {r.matchCount > 0 && <span className="text-slate-400"> · {r.matchCount} application(s)</span>}
              </p>
              {r.notes && <p className="text-[11px] text-amber-700 mt-1">{r.notes}</p>}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Switch checked={r.isActive !== false} onCheckedChange={() => toggleActive(r)} data-testid={`expense-rule-toggle-${r.id}`} />
              <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`expense-rule-edit-${r.id}`}>
                <Edit3 className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" className="text-red-600" onClick={() => remove(r)} data-testid={`expense-rule-delete-${r.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-1">
          <Repeat className="h-4 w-4 text-violet-600" /> Charges récurrentes repérées
        </h3>
        <p className="text-xs text-slate-500 mb-3">
          Dépenses revenant sur plusieurs mois. Créez la règle en un clic pour qu&apos;elles soient
          reconnues dès le prochain import.
        </p>
        {recurring.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center border rounded-lg" data-testid="expense-recurring-empty">
            Aucune récurrence détectée pour l&apos;instant.
          </p>
        ) : (
          <div className="space-y-2" data-testid="expense-recurring-list">
            {recurring.map((g) => (
              <div key={g.key} className="border rounded-lg p-3 flex items-start justify-between gap-4" data-testid={`expense-recurring-${g.key.replace(/\s/g, '-')}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{g.label}</p>
                  <p className="text-xs text-slate-500">
                    {SOURCE_LABELS[g.source]} · {g.months.length} mois · {g.occurrences} opération(s) ·
                    {' '}moyenne {fmt(g.averageAmount)} XOF
                    {g.isStableAmount
                      ? <span className="text-emerald-600"> · montant stable</span>
                      : <span className="text-amber-600"> · montant variable ({fmt(g.minAmount)} → {fmt(g.maxAmount)})</span>}
                  </p>
                  {g.suggestedCategoryLabel && (
                    <p className="text-[11px] text-slate-500 mt-0.5">Déjà classé en : {g.suggestedCategoryLabel}</p>
                  )}
                </div>
                <div className="shrink-0">
                  {g.coveredByRule ? (
                    <Badge variant="outline" className="text-[10px]">déjà couverte</Badge>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => openCreate(g.draftRule)} data-testid={`expense-recurring-create-${g.key.replace(/\s/g, '-')}`}>
                      <Sparkles className="h-3 w-3 mr-1" /> Créer la règle
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="expense-rule-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Modifier la règle' : 'Nouvelle règle de détection'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nom de la règle *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex : Salaires du mois"
                data-testid="expense-rule-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>S&apos;applique à</Label>
                <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                  <SelectTrigger data-testid="expense-rule-source-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="both">Banque + Wave</SelectItem>
                    <SelectItem value="bank">Relevé bancaire</SelectItem>
                    <SelectItem value="wave">Wave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Sens de l&apos;opération</Label>
                <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                  <SelectTrigger data-testid="expense-rule-direction-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="debit">Sorties (dépenses)</SelectItem>
                    <SelectItem value="credit">Entrées</SelectItem>
                    <SelectItem value="any">Tous sens</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Mots du libellé, séparés par une virgule</Label>
              <Input
                value={form.keywords}
                onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                placeholder="ORDRE DE VIREMENT MULTIPLE SUIVA, virement multiple"
                data-testid="expense-rule-keywords-input"
              />
              <p className="text-[11px] text-slate-500">Accents et majuscules sont ignorés.</p>
            </div>

            <div className="space-y-2">
              <Label>Bénéficiaires Wave (numéro ou nom), séparés par une virgule</Label>
              <Textarea
                rows={2}
                value={form.beneficiaries}
                onChange={(e) => setForm({ ...form, beneficiaries: e.target.value })}
                placeholder="0708091011, 0501020304, KOUAME Yao"
                data-testid="expense-rule-beneficiaries-input"
              />
              <p className="text-[11px] text-slate-500">
                Détection certaine pour les salaires et le loyer : déclarez les numéros une fois pour toutes.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Critère de montant</Label>
              <Select value={form.amountMode} onValueChange={(v) => setForm({ ...form, amountMode: v })}>
                <SelectTrigger data-testid="expense-rule-amountmode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Aucun — le libellé suffit</SelectItem>
                  <SelectItem value="min">Montant minimum</SelectItem>
                  <SelectItem value="expected">Montant attendu avec une marge</SelectItem>
                </SelectContent>
              </Select>
              {form.amountMode === 'min' && (
                <Input
                  type="number" value={form.amountMin}
                  onChange={(e) => setForm({ ...form, amountMin: e.target.value })}
                  placeholder="5000000"
                  data-testid="expense-rule-amountmin-input"
                />
              )}
              {form.amountMode === 'expected' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Montant attendu (XOF)</Label>
                    <Input
                      type="number" value={form.amountExpected}
                      onChange={(e) => setForm({ ...form, amountExpected: e.target.value })}
                      placeholder="12500000"
                      data-testid="expense-rule-amountexpected-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Marge acceptée (%)</Label>
                    <Input
                      type="number" value={form.tolerancePercent}
                      onChange={(e) => setForm({ ...form, tolerancePercent: e.target.value })}
                      data-testid="expense-rule-tolerance-input"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Catégorie à appliquer *</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="expense-rule-category-select">
                    <SelectValue placeholder="Choisir une catégorie" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {categories.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Libellé affiché (facultatif)</Label>
                <Input
                  value={form.displayLabel}
                  onChange={(e) => setForm({ ...form, displayLabel: e.target.value })}
                  placeholder="Gratification Salariale"
                  data-testid="expense-rule-displaylabel-input"
                />
              </div>
            </div>

            <div className="flex items-center justify-between border rounded-lg px-3 py-2">
              <div>
                <p className="text-sm text-slate-800">Rattacher les frais Wave au paiement</p>
                <p className="text-[11px] text-slate-500">Les frais pris en charge suivent la même catégorie.</p>
              </div>
              <Switch
                checked={!!form.includeFees}
                onCheckedChange={(v) => setForm({ ...form, includeFees: v })}
                data-testid="expense-rule-includefees-switch"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Priorité</Label>
                <Input
                  type="number" value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  data-testid="expense-rule-priority-input"
                />
                <p className="text-[11px] text-slate-500">La priorité la plus élevée tranche en cas de conflit.</p>
              </div>
              <div className="space-y-2">
                <Label>Note interne</Label>
                <Input
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="expense-rule-notes-input"
                />
              </div>
            </div>

            {testResult && (
              <div className="border rounded-lg p-3 bg-slate-50" data-testid="expense-rule-test-result">
                <p className="text-sm font-medium text-slate-800">
                  {testResult.matched} opération(s) reconnue(s) dans l&apos;historique
                </p>
                {testResult.matched === 0 && (
                  <p className="text-xs text-amber-700 flex items-center gap-1 mt-1">
                    <AlertTriangle className="h-3 w-3" /> Aucun historique ne correspond : vérifiez le libellé ou le montant.
                  </p>
                )}
                <ul className="mt-2 space-y-1">
                  {(testResult.samples || []).map((s, i) => (
                    <li key={i} className="text-xs text-slate-600 truncate">
                      {s.source} · {s.label} · {fmt(s.amount)} XOF
                      {s.currentCategory ? ` · déjà classé : ${s.currentCategory}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={runTest} data-testid="expense-rule-test-btn">
              <FlaskConical className="h-4 w-4 mr-1" /> Tester sur l&apos;historique
            </Button>
            <Button onClick={save} disabled={saving} data-testid="expense-rule-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              {editingId ? 'Enregistrer' : 'Créer la règle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
